import { useEffect } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { RedirectIfAuthed, RequireAuth, RequireOnboarded } from "./components/Guards";
import { ToastProvider } from "./components/Toast";
import { Account } from "./pages/Account";
import { Apply } from "./pages/Apply";
import { Contact } from "./pages/Contact";
import { Earnings } from "./pages/Earnings";
import { ForgotPassword } from "./pages/ForgotPassword";
import { HowItWorks } from "./pages/HowItWorks";
import { Landing } from "./pages/Landing";
import { Log } from "./pages/Log";
import { Login } from "./pages/Login";
import { Privacy } from "./pages/Privacy";
import { ResetPassword } from "./pages/ResetPassword";
import { TermsPage } from "./pages/TermsPage";
import { OnboardingFinish } from "./pages/OnboardingFinish";
import { OnboardingProfile } from "./pages/OnboardingProfile";
import { OnboardingTerms } from "./pages/OnboardingTerms";
import { Record } from "./pages/Record";
import { AdminApplications } from "./pages/AdminApplications";
import { AdminContent } from "./pages/AdminContent";
import { AdminContributor } from "./pages/AdminContributor";
import { AdminContributors } from "./pages/AdminContributors";
import { AdminInvites } from "./pages/AdminInvites";
import { AdminPayouts } from "./pages/AdminPayouts";
import { Review } from "./pages/Review";
import { Signup } from "./pages/Signup";
import { useAuth } from "./store/auth";
import { useConfig } from "./store/config";

const LANDING_SECTIONS = new Set(["how", "payment", "rights", "faq"]);

/** A new page opens at the top. Landing section links still scroll to their heading. */
function ScrollToTop() {
  const { pathname, hash, key } = useLocation();

  useEffect(() => {
    if (pathname === "/" && LANDING_SECTIONS.has(hash.replace("#", ""))) return;
    const root = document.documentElement;
    const previous = root.style.scrollBehavior;
    root.style.scrollBehavior = "auto";
    window.scrollTo(0, 0);
    if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
    document.querySelectorAll<HTMLElement>(".site, .app-shell").forEach((node) => {
      node.scrollTop = 0;
    });
    root.style.scrollBehavior = previous;
  }, [pathname, hash, key]);

  return null;
}

export default function App() {
  const bootstrap = useAuth((state) => state.bootstrap);
  const loadConfig = useConfig((state) => state.load);

  // Resolve the stored session once, before any guard runs, so a reload does
  // not bounce a signed-in contributor through /login on its way back.
  useEffect(() => {
    void bootstrap();
    // The API's own limits and options, so no screen has to hardcode them.
    void loadConfig();
  }, [bootstrap, loadConfig]);

  return (
    <ToastProvider>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Landing />} />

        {/* The link shared on LinkedIn and elsewhere. No account, no guard —
            signed-in contributors can open it too, which is harmless. */}
        <Route path="/apply" element={<Apply />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        {/* Not behind a sign-in guard: somebody resetting a password may
            still have a live session, and bouncing them away would strand the link. */}
        <Route path="/reset-password" element={<ResetPassword />} />

        <Route
          path="/signup"
          element={
            <RedirectIfAuthed>
              <Signup />
            </RedirectIfAuthed>
          }
        />
        <Route
          path="/login"
          element={
            <RedirectIfAuthed>
              <Login />
            </RedirectIfAuthed>
          }
        />

        <Route
          path="/onboarding/profile"
          element={
            <RequireAuth>
              <OnboardingProfile />
            </RequireAuth>
          }
        />
        <Route
          path="/onboarding/terms"
          element={
            <RequireAuth>
              <OnboardingTerms />
            </RequireAuth>
          }
        />
        <Route
          path="/onboarding/finish"
          element={
            <RequireAuth>
              <OnboardingFinish />
            </RequireAuth>
          }
        />

        <Route
          path="/record"
          element={
            <RequireOnboarded>
              <Record />
            </RequireOnboarded>
          }
        />
        <Route
          path="/log"
          element={
            <RequireOnboarded>
              <Log />
            </RequireOnboarded>
          }
        />
        <Route
          path="/earnings"
          element={
            <RequireOnboarded>
              <Earnings />
            </RequireOnboarded>
          }
        />
        <Route
          path="/account"
          element={
            <RequireOnboarded>
              <Account />
            </RequireOnboarded>
          }
        />

        {/* Back office. Its own credentials, so no contributor guard. */}
        <Route path="/review" element={<Review />} />
        <Route path="/admin/content" element={<AdminContent />} />
        <Route path="/admin/payouts" element={<AdminPayouts />} />
        <Route path="/admin/invites" element={<AdminInvites />} />
        <Route path="/admin/applications" element={<AdminApplications />} />
        <Route path="/admin/contributors" element={<AdminContributors />} />
        <Route
          path="/admin/contributors/:contributorId"
          element={<AdminContributor />}
        />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ToastProvider>
  );
}
